import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Avatar,
  Box,
  Divider,
  Button,
  IconButton,
  Badge,
  Menu,
  MenuItem,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { useNavigate } from 'react-router-dom';
import logo from '../images/appbarlogo.png';
import useAdminNotifications from './useAdminNotifications'; // Reuse the notification hook

const AdminAppBar = ({ user }) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const { notifications, unreadCount, markNotificationsAsRead } = useAdminNotifications(user); // Get notifications and unread count

  const handleLogout = () => {
    navigate('/'); // Navigate to the login page
  };

  const handleNotificationClick = (event) => {
    setAnchorEl(event.currentTarget);
    markNotificationsAsRead(); // Reset unread count
  };

  const handleNotificationClose = () => {
    setAnchorEl(null);
  };

  return (
    <AppBar
      position="relative"
      sx={{
        height: 64,
        margin: '16px',
        backgroundColor: '#FFFFFF',
        boxShadow: '0px 3px 5px rgba(0, 0, 0, 0.1)',
        borderRadius: '16px',
        width: 'calc(100% - 32px)',
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}
    >
      <Toolbar sx={{ display: 'flex', alignItems: 'center', padding: '0 16px', height: '100%' }}>
        {/* Logo */}
        <Box sx={{ display: 'flex', alignItems: 'left' }}>
          <img src={logo} alt="Admin Logo" style={{ width: '70px', height: '60px' }} />
        </Box>

        {/* Spacer to push items to the right */}
        <Box sx={{ flexGrow: 1 }} />

        {/* Notifications Button */}
        <IconButton
          onClick={handleNotificationClick}
          sx={{ color: '#39B54A', marginX: '16px' }}
        >
          <Badge badgeContent={unreadCount} color="primary" invisible={unreadCount === 0}>
            <NotificationsIcon />
          </Badge>
        </IconButton>

        {/* Notifications Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleNotificationClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {notifications.length > 0 ? (
            notifications.map((notification, index) => (
              <MenuItem key={index} onClick={handleNotificationClose}>
                <Box sx={{ display: 'flex', flexDirection: 'row', maxWidth: '85%' }}>
                  <NotificationsIcon sx={{ color: '#39B54A' }} />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      fontSize: '12px',
                      marginTop: '4px',
                    }}
                  >
                    {notification}
                  </Typography>
                </Box>
              </MenuItem>
            ))
          ) : (
            <MenuItem onClick={handleNotificationClose}>No new notifications</MenuItem>
          )}
        </Menu>

        {/* Log Out Button */}
        <Box sx={{ marginX: '16px' }}>
          <Button
            variant="outlined"
            onClick={handleLogout}
            sx={{
              color: '#39B54A',
              borderColor: '#39B54A',
              '&:hover': { borderColor: '#2c8f39', color: '#2c8f39' },
            }}
          >
            Log Out
          </Button>
        </Box>

        {/* Divider */}
        <Divider orientation="vertical" flexItem sx={{ marginX: '16px', marginY: '5px', height: '50px' }} />

        {/* User Info */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar src={user?.avatar} sx={{ marginRight: '8px' }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', textAlign: 'left' }}>
            <Typography variant="body1" color="textSecondary">{user?.name || 'User Name'}</Typography>
            <Typography variant="body2" color="textSecondary">{user?.position || 'user@example.com'}</Typography>
          </Box>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default AdminAppBar;
